import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { TCampaign } from '@/typings/api/campaigns.type'
import {
  GetInstagramInstantwinPrizesParams,
  InstantwinPrizeDetail,
  PostInstagramInstantwinPrizeParams,
  TPrize
} from '@/typings/api/instagram_instantwin_prizes.schema'

import { getCampaign } from '../api/getCampaign'
import { getInstagramInstantwinPrize } from '../api/getInstagramInstantwinPrize'
import { getInstagramInstantwinPrizes } from '../api/getInstagramInstantwinPrizes'
import { postInstagramInstantwinPrize } from '../api/postInstagramInstantwinPrize'

export const useInstagramInstantwinPrizeStore = defineStore('InstagramInstantwinPrizeStore', () => {
  // State
  const prizes = ref<TPrize[]>([])
  const prize = ref<InstantwinPrizeDetail | null>(null)
  const total = ref(0)
  const totalApplicantCount = ref(0)
  const hasRunningCampaignOnSameAccount = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const campaign = ref<TCampaign | null>(null)
  const firstLoaded = ref(false)

  // Parameters
  const params = ref<GetInstagramInstantwinPrizesParams>({
    campaignId: 0,
    sortBy: '-applicantCount',
    pagingNo: 1,
    limitCount: 25
  })

  // Computed
  const options = computed(() => ({
    total: total.value,
    pagingNo: params.value.pagingNo || 1,
    limitCount: params.value.limitCount || 25,
    sortBy: params.value.sortBy || '-applicantCount'
  }))

  // Actions
  const setParams = (newParams: Partial<GetInstagramInstantwinPrizesParams>) => {
    params.value = { ...params.value, ...newParams }
  }

  const fetchPrizes = async () => {
    try {
      loading.value = true
      error.value = null

      const response = await getInstagramInstantwinPrizes(params.value)

      prizes.value = response.prizes
      total.value = response.total
      totalApplicantCount.value = response.totalApplicantCount
      hasRunningCampaignOnSameAccount.value = response.hasRunningCampaignOnSameAccount
    } catch {
      error.value = 'データの取得に失敗しました'
    } finally {
      firstLoaded.value = true
      loading.value = false
    }
  }

  const fetchPrize = async (prizeId: number) => {
    try {
      loading.value = true
      error.value = null

      prize.value = await getInstagramInstantwinPrize(prizeId)
    } catch {
      error.value = 'データの取得に失敗しました'
    } finally {
      loading.value = false
    }
  }

  const fetchCampaign = async () => {
    try {
      error.value = null
      campaign.value = await getCampaign(params.value.campaignId)
    } catch {
      error.value = 'キャンペーン情報の取得に失敗しました'
      firstLoaded.value = true
    }
  }

  const createPrize = async (createParams: PostInstagramInstantwinPrizeParams) => {
    try {
      loading.value = true
      error.value = null

      const created = await postInstagramInstantwinPrize(createParams)
      await fetchPrizes()
      await fetchCampaign()
      return created
    } catch (err) {
      error.value = '応募グループの作成に失敗しました'
      throw err
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    prizes.value = []
    total.value = 0
    totalApplicantCount.value = 0
    hasRunningCampaignOnSameAccount.value = false
    loading.value = false
    error.value = null
    campaign.value = null
    firstLoaded.value = false
    params.value = {
      campaignId: 0,
      sortBy: '-applicantCount',
      pagingNo: 1,
      limitCount: 25
    }
  }

  return {
    // State
    prizes,
    prize,
    total,
    totalApplicantCount,
    hasRunningCampaignOnSameAccount,
    loading,
    error,
    campaign,
    firstLoaded,
    params,

    // Computed
    options,

    // Actions
    setParams,
    fetchPrizes,
    fetchPrize,
    fetchCampaign,
    createPrize,
    reset
  }
})
